import {
  Component,
  OnDestroy,
  OnInit,
  Signal,
  ViewEncapsulation,
  ChangeDetectionStrategy,
} from '@angular/core';
import { Permission } from 'src/app/shared/constants/permissions';
import { ChartType } from 'src/app/tickets/types/chart-type.types';
import { KpiChart } from '../../../types/download-charts-pdf.types';

@Component({
  selector: 'app-download-charts-pdf-dialog',
  encapsulation: ViewEncapsulation.None,
  template: `
    <section
      [id]="containerId"
      class="pdf-ticket-report font-lato box-border grid w-300! min-w-300 gap-2.5 bg-gray-50 p-6 **:box-border"
    >
      <header class="flex items-end justify-between pb-1">
        <div>
          <h1 class="m-0 text-3xl leading-tight font-bold text-black">
            Ticket Insights
          </h1>
          <p class="m-0 mt-1 text-sm text-gray-400">
            Live overview of ticket volumes, satisfaction scores, and resolution
            times.
          </p>
        </div>
        <p class="m-0 text-xs font-semibold text-gray-400 uppercase">
          {{ today | date: 'MMMM d, y' }}
        </p>
      </header>

      <section
        class="border-gray-150 grid grid-cols-4 overflow-hidden border bg-white"
        aria-label="Key ticket metrics"
      >
        <ng-container *ngxPermissionsOnly="Permission.SERVICES">
          @if (chartAt(1, 0); as chart) {
            <article class="report-kpi border-gray-150 border-r">
              <h2>
                <span
                  class="material-icons-outlined"
                  style="font-size:16px"
                  aria-hidden="true"
                  >lan</span
                >
                Active services
              </h2>
              <strong>{{ chart.data.value }}</strong>
              <span>{{
                chart.data.value > 0 ? 'All operational' : 'No active services'
              }}</span>
            </article>
          }
        </ng-container>
        <ng-container *ngxPermissionsOnly="Permission.ORDERS">
          @if (chartAt(1, 1); as chart) {
            <article class="report-kpi border-gray-150 border-r">
              <h2>
                <span
                  class="material-icons-outlined"
                  style="font-size:16px"
                  aria-hidden="true"
                  >local_shipping</span
                >
                Inflight orders
              </h2>
              <strong>{{ chart.data.value }}</strong>
              <span>{{
                chart.data.value > 0 ? 'In progress' : 'Queue is clear'
              }}</span>
            </article>
          }
        </ng-container>
        @if (chartAt(5, 0); as chart) {
          <article class="report-kpi border-gray-150 border-r">
            <h2>
              <span
                class="material-icons-outlined"
                style="font-size:16px"
                aria-hidden="true"
                >emergency</span
              >
              Emergency tickets
            </h2>
            <strong>{{ chart.data.value }}</strong>
            <span>{{
              chart.data.value > 0 ? 'Needs immediate attention' : 'None active'
            }}</span>
          </article>
        }
        @if (chartAt(5, 1); as chart) {
          <article class="report-kpi">
            <h2>
              <span
                class="material-icons-outlined"
                style="font-size:16px"
                aria-hidden="true"
                >build</span
              >
              Scheduled maintenance tickets
            </h2>
            <strong>{{ chart.data.value }}</strong>
            <span>{{
              chart.data.value > 0 ? 'Scheduled' : 'None scheduled'
            }}</span>
          </article>
        }
      </section>

      <div class="grid grid-cols-2 gap-2.5">
        <section
          class="report-panel min-w-0"
          aria-label="Customer satisfaction"
        >
          <h2 class="flex items-center gap-1.5">
            <span
              class="material-icons-outlined"
              style="font-size:20px"
              aria-hidden="true"
              >sentiment_satisfied_alt</span
            >
            Customer satisfaction
          </h2>
          <div class="grid grid-cols-2 divide-x divide-gray-100">
            @if (chartAt(0, 1); as chart) {
              <div class="pdf-csat-gauge flex flex-col items-center py-3">
                @if (chart.loading) {
                  <div
                    class="h-20 w-20 animate-pulse rounded-full bg-gray-100"
                  ></div>
                } @else if ($any(chart.data).records === 0) {
                  <h3
                    class="font-lato m-0 self-start text-xs font-normal text-gray-400"
                  >
                    {{ chart.data.title }}
                  </h3>
                  <div
                    class="flex h-[160px] flex-col items-center justify-center gap-1.5"
                  >
                    <span
                      class="material-icons-outlined text-[2rem] text-gray-300"
                      >bar_chart</span
                    >
                    <span class="text-sm font-semibold text-gray-400"
                      >No data available</span
                    >
                    <span class="text-xs text-gray-300"
                      >Nothing to display for this period</span
                    >
                  </div>
                } @else {
                  <ngx-gauge
                    [value]="chart.data.value"
                    [min]="$any(chart.data).minValue"
                    [max]="$any(chart.data).maxValue"
                    [type]="'arch'"
                    [cap]="'butt'"
                    [thick]="10"
                    [size]="160"
                    [margin]="28"
                    [thresholds]="$any(chart.data).thresholds"
                    [style.color]="
                      getThresholdColor(
                        $any(chart.data).thresholds,
                        chart.data.value
                      )
                    "
                    [label]="''"
                    [markers]="$any(chart.data).markers"
                  ></ngx-gauge>
                  <div class="-mt-5 text-sm font-semibold">NPS</div>
                  <div class="mt-0.5 text-xs text-gray-400">
                    {{ chart.data.value }} / {{ $any(chart.data).maxValue }}
                  </div>
                }
              </div>
            }
            @if (chartAt(0, 2); as chart) {
              <div class="pdf-csat-gauge flex flex-col items-center py-3">
                @if (chart.loading) {
                  <div
                    class="h-20 w-20 animate-pulse rounded-full bg-gray-100"
                  ></div>
                } @else if ($any(chart.data).records === 0) {
                  <h3
                    class="font-lato m-0 self-start text-xs font-normal text-gray-400"
                  >
                    {{ chart.data.title }}
                  </h3>
                  <div
                    class="flex h-[160px] flex-col items-center justify-center gap-1.5"
                  >
                    <span
                      class="material-icons-outlined text-[2rem] text-gray-300"
                      >bar_chart</span
                    >
                    <span class="text-sm font-semibold text-gray-400"
                      >No data available</span
                    >
                    <span class="text-xs text-gray-300"
                      >Nothing to display for this period</span
                    >
                  </div>
                } @else {
                  <ngx-gauge
                    [value]="chart.data.value"
                    [min]="$any(chart.data).minValue"
                    [max]="$any(chart.data).maxValue"
                    [type]="'arch'"
                    [cap]="'butt'"
                    [thick]="10"
                    [size]="160"
                    [margin]="28"
                    [thresholds]="$any(chart.data).thresholds"
                    [style.color]="
                      getThresholdColor(
                        $any(chart.data).thresholds,
                        chart.data.value
                      )
                    "
                    [label]="''"
                    [markers]="$any(chart.data).markers"
                  ></ngx-gauge>
                  <div class="-mt-5 text-sm font-semibold">CSAT</div>
                  <div class="mt-0.5 text-xs text-gray-400">
                    {{ chart.data.value }} / {{ $any(chart.data).maxValue }}
                  </div>
                }
              </div>
            }
          </div>
        </section>

        <section class="report-panel min-w-0" aria-label="Resolution time">
          <h2 class="flex items-center gap-1.5">
            <span
              class="material-icons-outlined"
              style="font-size:20px"
              aria-hidden="true"
              >schedule</span
            >
            Resolution time
          </h2>
          @if (chartAt(0, 0); as chart) {
            <div class="pdf-csat-gauge flex flex-col items-center py-3">
              @if (chart.loading) {
                <div
                  class="h-20 w-20 animate-pulse rounded-full bg-gray-100"
                ></div>
              } @else if ($any(chart.data).records === 0) {
                <h3
                  class="font-lato m-0 self-start text-xs font-normal text-gray-400"
                >
                  {{ chart.data.title }}
                </h3>
                <div
                  class="flex h-[160px] flex-col items-center justify-center gap-1.5"
                >
                  <span
                    class="material-icons-outlined text-[2rem] text-gray-300"
                    >bar_chart</span
                  >
                  <span class="text-sm font-semibold text-gray-400"
                    >No data available</span
                  >
                  <span class="text-xs text-gray-300"
                    >Nothing to display for this period</span
                  >
                </div>
              } @else {
                <ngx-gauge
                  [value]="chart.data.value"
                  [min]="$any(chart.data).minValue"
                  [max]="$any(chart.data).maxValue"
                  [type]="'arch'"
                  [cap]="'butt'"
                  [thick]="10"
                  [size]="160"
                  [margin]="28"
                  [thresholds]="$any(chart.data).thresholds"
                  [style.color]="
                    getThresholdColor(
                      $any(chart.data).thresholds,
                      chart.data.value
                    )
                  "
                  [label]="''"
                  [markers]="$any(chart.data).markers"
                ></ngx-gauge>
                <div class="-mt-5 text-sm font-semibold">Days</div>
                <div class="mt-0.5 text-xs text-gray-400">
                  {{ chart.data.value }} / {{ $any(chart.data).maxValue }}
                </div>
              }
            </div>
          }
        </section>
      </div>

      <section class="grid grid-cols-5 gap-2.5" aria-label="Ticket counters">
        @if (chartAt(1, 2); as recent) {
          @if (chartAt(2, 0); as total) {
            <article class="report-counter">
              <h2>Closed tickets</h2>
              <small>Last 30 days</small>
              <div>
                <strong
                  [ngClass]="recent.ngClass"
                  [style.color]="
                    recent.data.value === 0 ? '#FF5A5F' : '#61F29D'
                  "
                  >{{ recent.data.value }}</strong
                >
                <span>{{ total.data.value }} all time</span>
              </div>
              <em
                ><i
                  [style.width.%]="
                    progressPercent(recent.data.value, total.data.value)
                  "
                ></i
              ></em>
            </article>
          }
        }
        @if (chartAt(2, 1); as overdue) {
          @if (chartAt(2, 2); as total) {
            <article class="report-counter">
              <h2>General inquiry tickets</h2>
              <small>Older than 30 days</small>
              <div>
                <strong
                  [ngClass]="overdue.ngClass"
                  [style.color]="overdue.data.value > 0 ? '#ea580c' : null"
                  >{{ overdue.data.value }}</strong
                >
                <span>{{ total.data.value }} total</span>
              </div>
              <em
                ><i
                  [style.width.%]="
                    progressPercent(overdue.data.value, total.data.value)
                  "
                ></i
              ></em>
            </article>
          }
        }
        @if (chartAt(3, 0); as overdue) {
          @if (chartAt(3, 1); as total) {
            <article class="report-counter">
              <h2>Non-proactive tickets</h2>
              <small>Older than 30 days</small>
              <div>
                <strong
                  [ngClass]="overdue.ngClass"
                  [style.color]="overdue.data.value > 0 ? '#ea580c' : null"
                  >{{ overdue.data.value }}</strong
                >
                <span>{{ total.data.value }} total</span>
              </div>
              <em
                ><i
                  [style.width.%]="
                    progressPercent(overdue.data.value, total.data.value)
                  "
                ></i
              ></em>
            </article>
          }
        }
        @if (chartAt(3, 2); as overdue) {
          @if (chartAt(4, 0); as total) {
            <article class="report-counter">
              <h2>Support (NOC) tickets</h2>
              <small>Older than 30 days</small>
              <div>
                <strong
                  [ngClass]="overdue.ngClass"
                  [style.color]="overdue.data.value > 0 ? '#ea580c' : null"
                  >{{ overdue.data.value }}</strong
                >
                <span>{{ total.data.value }} total</span>
              </div>
              <em
                ><i
                  [style.width.%]="
                    progressPercent(overdue.data.value, total.data.value)
                  "
                ></i
              ></em>
            </article>
          }
        }
        @if (chartAt(4, 1); as overdue) {
          @if (chartAt(4, 2); as total) {
            <article class="report-counter">
              <h2>Billing &amp; payment tickets</h2>
              <small>Older than 30 days</small>
              <div>
                <strong
                  [ngClass]="overdue.ngClass"
                  [style.color]="overdue.data.value > 0 ? '#ea580c' : null"
                  >{{ overdue.data.value }}</strong
                >
                <span>{{ total.data.value }} total</span>
              </div>
              <em
                ><i
                  [style.width.%]="
                    progressPercent(overdue.data.value, total.data.value)
                  "
                ></i
              ></em>
            </article>
          }
        }
      </section>

      <div class="grid grid-cols-[2fr_1fr] gap-2.5">
        @if (chartAt(6, 0); as chart) {
          <app-line-values
            class="pdf-report-line min-w-0"
            [ngClass]="chart.ngClass"
            [data]="chart.data"
            [loading]="chart.loading"
          ></app-line-values>
        }

        <section class="flex min-w-0 flex-col bg-white p-6">
          <h2
            class="font-montserrat m-0 inline-flex items-center gap-2 text-base font-bold uppercase"
          >
            <span
              class="material-icons-outlined"
              style="height:20px;width:20px;font-size:20px;line-height:20px"
              >checklist</span
            >
            Ticket breakdown
          </h2>
          <ul
            class="mt-2 flex flex-1 flex-col justify-between"
            style="list-style:none;padding:0"
          >
            @if (chartAt(1, 2); as recent) {
              @if (chartAt(2, 0); as total) {
                <li
                  class="grid items-center gap-4 border-b border-gray-200 py-2"
                  style="grid-template-columns:minmax(0,1fr) auto"
                >
                  <span
                    class="font-lato flex min-w-0 items-center gap-2 text-sm"
                  >
                    <span
                      style="display:inline-block;height:13px;width:13px;min-width:13px;border-radius:50%;flex-shrink:0"
                      [style.background]="
                        recent.data.value === 0 ? '#FF5A5F' : '#61F29D'
                      "
                    ></span>
                    Closed Tickets <span class="text-gray-400">(last 30d)</span>
                  </span>
                  <span
                    class="font-lato leading-none font-bold whitespace-nowrap"
                    [style.color]="
                      recent.data.value === 0 ? '#FF5A5F' : '#61F29D'
                    "
                    >{{ recent.data.value }} / {{ total.data.value }}</span
                  >
                </li>
              }
            }
            @if (chartAt(2, 1); as overdue) {
              @if (chartAt(2, 2); as total) {
                <li
                  class="grid items-center gap-4 border-b border-gray-200 py-2"
                  style="grid-template-columns:minmax(0,1fr) auto"
                >
                  <span
                    class="font-lato flex min-w-0 items-center gap-2 text-sm"
                  >
                    <span
                      style="display:inline-block;height:13px;width:13px;min-width:13px;border-radius:50%;flex-shrink:0"
                      [style.background]="
                        overdue.data.value === 0 ? '#61F29D' : '#FF5A5F'
                      "
                    ></span>
                    General Inquiry <span class="text-gray-400">(&gt;30d)</span>
                  </span>
                  <span
                    class="font-lato leading-none font-bold whitespace-nowrap"
                    [style.color]="
                      overdue.data.value === 0 ? '#61F29D' : '#FF5A5F'
                    "
                    >{{ overdue.data.value }} / {{ total.data.value }}</span
                  >
                </li>
              }
            }
            @if (chartAt(3, 0); as overdue) {
              @if (chartAt(3, 1); as total) {
                <li
                  class="grid items-center gap-4 border-b border-gray-200 py-2"
                  style="grid-template-columns:minmax(0,1fr) auto"
                >
                  <span
                    class="font-lato flex min-w-0 items-center gap-2 text-sm"
                  >
                    <span
                      style="display:inline-block;height:13px;width:13px;min-width:13px;border-radius:50%;flex-shrink:0"
                      [style.background]="
                        overdue.data.value === 0 ? '#61F29D' : '#FF5A5F'
                      "
                    ></span>
                    Non-proactive <span class="text-gray-400">(&gt;30d)</span>
                  </span>
                  <span
                    class="font-lato leading-none font-bold whitespace-nowrap"
                    [style.color]="
                      overdue.data.value === 0 ? '#61F29D' : '#FF5A5F'
                    "
                    >{{ overdue.data.value }} / {{ total.data.value }}</span
                  >
                </li>
              }
            }
            @if (chartAt(3, 2); as overdue) {
              @if (chartAt(4, 0); as total) {
                <li
                  class="grid items-center gap-4 border-b border-gray-200 py-2"
                  style="grid-template-columns:minmax(0,1fr) auto"
                >
                  <span
                    class="font-lato flex min-w-0 items-center gap-2 text-sm"
                  >
                    <span
                      style="display:inline-block;height:13px;width:13px;min-width:13px;border-radius:50%;flex-shrink:0"
                      [style.background]="
                        overdue.data.value === 0 ? '#61F29D' : '#FF5A5F'
                      "
                    ></span>
                    Support (NOC) <span class="text-gray-400">(&gt;30d)</span>
                  </span>
                  <span
                    class="font-lato leading-none font-bold whitespace-nowrap"
                    [style.color]="
                      overdue.data.value === 0 ? '#61F29D' : '#FF5A5F'
                    "
                    >{{ overdue.data.value }} / {{ total.data.value }}</span
                  >
                </li>
              }
            }
            @if (chartAt(4, 1); as overdue) {
              @if (chartAt(4, 2); as total) {
                <li
                  class="grid items-center gap-4 border-b border-gray-200 py-2"
                  style="grid-template-columns:minmax(0,1fr) auto"
                >
                  <span
                    class="font-lato flex min-w-0 items-center gap-2 text-sm"
                  >
                    <span
                      style="display:inline-block;height:13px;width:13px;min-width:13px;border-radius:50%;flex-shrink:0"
                      [style.background]="
                        overdue.data.value === 0 ? '#61F29D' : '#FF5A5F'
                      "
                    ></span>
                    Billing &amp; Payment
                    <span class="text-gray-400">(&gt;30d)</span>
                  </span>
                  <span
                    class="font-lato leading-none font-bold whitespace-nowrap"
                    [style.color]="
                      overdue.data.value === 0 ? '#61F29D' : '#FF5A5F'
                    "
                    >{{ overdue.data.value }} / {{ total.data.value }}</span
                  >
                </li>
              }
            }
            @if (chartAt(5, 0); as chart) {
              <li
                class="grid items-center gap-4 border-b border-gray-200 py-2"
                style="grid-template-columns:minmax(0,1fr) auto"
              >
                <span class="font-lato flex min-w-0 items-center gap-2 text-sm">
                  <span
                    style="display:inline-block;height:13px;width:13px;min-width:13px;border-radius:50%;flex-shrink:0"
                    [style.background]="
                      chart.data.value === 0 ? '#61F29D' : '#FF5A5F'
                    "
                  ></span>
                  Emergency Tickets
                </span>
                <span
                  class="font-lato leading-none font-bold whitespace-nowrap"
                  [style.color]="chart.data.value === 0 ? '#61F29D' : '#FF5A5F'"
                  >{{ chart.data.value }}</span
                >
              </li>
            }
            @if (chartAt(5, 1); as chart) {
              <li
                class="grid items-center gap-4 py-2"
                style="grid-template-columns:minmax(0,1fr) auto"
              >
                <span class="font-lato flex min-w-0 items-center gap-2 text-sm">
                  <span
                    style="display:inline-block;height:13px;width:13px;min-width:13px;border-radius:50%;flex-shrink:0"
                    [style.background]="
                      chart.data.value === 0 ? '#61F29D' : '#FF5A5F'
                    "
                  ></span>
                  Scheduled Maintenance Tickets
                </span>
                <span
                  class="font-lato leading-none font-bold whitespace-nowrap"
                  [style.color]="chart.data.value === 0 ? '#61F29D' : '#FF5A5F'"
                  >{{ chart.data.value }}</span
                >
              </li>
            }
          </ul>
        </section>
      </div>

      <div class="grid grid-cols-1 gap-2.5">
        @if (chartAt(7, 0); as chart) {
          <app-horizontal-bars
            class="pdf-report-bars min-w-0"
            [ngClass]="chart.ngClass"
            [data]="chart.data"
            [loading]="chart.loading"
          ></app-horizontal-bars>
        }
      </div>
    </section>
  `,
  styleUrl: './download-charts-pdf-dialog.component.css',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class DownloadChartsPdfDialogComponent implements OnInit, OnDestroy {
  containerId: string = '';

  displayCharts: Signal<KpiChart>[][] = [];

  readonly today: Date = new Date();

  readonly ChartType = ChartType;

  readonly Permission = Permission;

  chartAt(groupIndex: number, chartIndex: number): KpiChart | undefined {
    return this.displayCharts[groupIndex]?.[chartIndex]?.();
  }

  progressPercent(value: number, total: number): number {
    return total > 0 ? Math.min((value / total) * 100, 100) : 0;
  }

  getThresholdColor(
    thresholds: Record<number, { color: string }> | null | undefined,
    value: number,
  ): string {
    if (!thresholds) {
      return '#000';
    }

    const keys = Object.keys(thresholds)
      .map(Number)
      .sort((a, b) => a - b);
    let color = '#000';
    for (const key of keys) {
      if (value >= key) color = thresholds[key].color;
    }
    return color;
  }

  ngOnInit(): void {
    document.documentElement.style.position = 'initial';
  }

  ngOnDestroy(): void {
    document.documentElement.style.position = null!;
  }
}
